import React, { useState, useEffect } from 'react';
import { storage, auth, db } from '../firebase';
import { ref, uploadBytes, getDownloadURL, getBytes, getMetadata, getBlob } from 'firebase/storage';
import { onAuthStateChanged } from 'firebase/auth';
import { getDoc, doc } from 'firebase/firestore';
import './SignsConverter.css';

const MAX_RETRIES = 3;
const RETRY_DELAY = 2000; // 2 seconds
const CHUNK_SIZE = 5 * 1024 * 1024; // 5MB chunks
const RATE_LIMIT_DELAY = 5000; // 5 seconds delay when hitting rate limits

const SignsConverter = () => {
  const [currentSigns, setCurrentSigns] = useState([]);
  const [csvFile, setCsvFile] = useState(null);
  const [convertedJson, setConvertedJson] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [retryCount, setRetryCount] = useState(0);
  const [downloadProgress, setDownloadProgress] = useState(0);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  const delay = ms => new Promise(resolve => setTimeout(resolve, ms));

  // Function to check if Firebase Storage is properly initialized
  const checkStorageAccess = async () => {
    try {
      // Try to list files in the signs directory to check if storage is accessible
      const signsRef = ref(storage, 'signs');
      await getMetadata(signsRef);
      console.log('Firebase Storage is accessible');
      return true;
    } catch (error) {
      if (error.code === 'storage/object-not-found') {
        // Directory doesn't exist yet, which is fine
        return true;
      }
      console.error('Firebase Storage access check failed:', error);
      return false;
    }
  };

  // Function to check if the user is properly authenticated
  const checkAuthentication = async () => {
    try {
      if (!auth.currentUser) {
        console.error('User is not authenticated');
        return false;
      }

      // Force token refresh to ensure it's not expired
      await auth.currentUser.getIdToken(true);
      
      // Log authentication details for debugging
      console.log('Authentication check passed:', {
        uid: auth.currentUser.uid,
        email: auth.currentUser.email,
        isAnonymous: auth.currentUser.isAnonymous,
        emailVerified: auth.currentUser.emailVerified,
      });
      
      return true;
    } catch (error) {
      console.error('Authentication check failed:', error);
      return false;
    }
  };

  // Function to check if the user has the necessary permissions to access Firebase Storage
  const checkUserPermissions = async () => {
    try {
      // Check if the user is authenticated
      const isAuth = await checkAuthentication();
      if (!isAuth) {
        setError('Authentication failed. Please sign in again.');
        setLoading(false);
        return false;
      }
      
      // Check if the user has admin privileges
      const userDoc = await getDoc(doc(db, 'users', auth.currentUser.uid));
      if (!userDoc.exists()) {
        setError('User document not found. Please contact support.');
        setLoading(false);
        return false;
      }
      
      const userData = userDoc.data();
      if (!userData.admin) {
        setError('You do not have permission to access this feature. Please contact an administrator.');
        setLoading(false);
        return false;
      }
      
      console.log('User has necessary permissions');
      return true;
    } catch (error) {
      console.error('Error checking user permissions:', error);
      setError('Failed to check user permissions. Please try again.');
      setLoading(false);
      return false;
    }
  };

  // Function to check if the signs directory exists and create it if it doesn't
  const ensureSignsDirectoryExists = async () => {
    try {
      // Check if storage is accessible
      const isStorageAccessible = await checkStorageAccess();
      if (!isStorageAccessible) {
        setError('Firebase Storage is not accessible. Please check your network connection and try again.');
        setLoading(false);
        return false;
      }
      
      // Check if the user has the necessary permissions
      const hasPermissions = await checkUserPermissions();
      if (!hasPermissions) {
        return false;
      }
      
      // Try to create a temporary file in the signs directory to check if it exists
      const tempRef = ref(storage, 'signs/.temp');
      try {
        // Try to get the metadata - if it fails with 'object-not-found', the directory doesn't exist
        await getMetadata(tempRef);
        console.log('Signs directory exists');
        return true;
      } catch (error) {
        if (error.code === 'storage/object-not-found') {
          console.log('Signs directory does not exist, creating it');
          // Create a temporary file to create the directory
          const tempData = 'temp';
          const blob = new Blob([tempData], { type: 'text/plain' });
          const file = new File([blob], '.temp', { type: 'text/plain' });
          await uploadBytes(tempRef, file);
          console.log('Signs directory created');
          return true;
        } else {
          console.error('Error checking if signs directory exists:', error);
          return false;
        }
      }
    } catch (error) {
      console.error('Error in ensureSignsDirectoryExists:', error);
      return false;
    }
  };

  // Function to check if the signs file exists and create it if it doesn't
  const ensureSignsFileExists = async () => {
    try {
      // Check authentication first
      const isAuth = await checkAuthentication();
      if (!isAuth) {
        setError('Authentication failed. Please sign in again.');
        setLoading(false);
        return false;
      }
      
      // Check if storage is accessible
      const isStorageAccessible = await checkStorageAccess();
      if (!isStorageAccessible) {
        setError('Firebase Storage is not accessible. Please check your network connection and try again.');
        setLoading(false);
        return false;
      }
      
      // Ensure the signs directory exists
      const isDirectoryExists = await ensureSignsDirectoryExists();
      if (!isDirectoryExists) {
        setError('Failed to create signs directory. Please check your permissions and try again.');
        setLoading(false);
        return false;
      }
      
      // Use the correct relative path
      const signsRef = ref(storage, 'signs/all-signs.json');
      try {
        // Try to get the metadata - if it fails with 'object-not-found', the file doesn't exist
        await getMetadata(signsRef);
        console.log('Signs file exists');
        return true;
      } catch (error) {
        if (error.code === 'storage/object-not-found') {
          console.log('Signs file does not exist, creating empty file');
          // Create an empty signs file
          const emptySigns = [];
          const jsonString = JSON.stringify(emptySigns);
          const blob = new Blob([jsonString], { type: 'application/json' });
          const file = new File([blob], 'all-signs.json', { type: 'application/json' });
          await uploadBytes(signsRef, file);
          console.log('Empty signs file created');
          return true;
        } else {
          console.error('Error checking if signs file exists:', error);
          return false;
        }
      }
    } catch (error) {
      console.error('Error in ensureSignsFileExists:', error);
      return false;
    }
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      console.log('Auth state changed:', user ? `User is signed in with email ${user.email}` : 'No user');
      setIsAuthenticated(!!user);
      if (user) {
        // Log the user's token for debugging
        const token = await user.getIdToken();
        console.log('User token:', token.substring(0, 20) + '...');
        // Ensure the signs file exists before fetching
        await ensureSignsFileExists();
        fetchCurrentSigns();
      } else {
        setError('Please sign in to access signs data');
        setLoading(false);
      }
    });

    return () => unsubscribe();
  }, []);

  // Function to create the signs file if it doesn't exist
  const createSignsFile = async () => {
    try {
      // First check if the file already exists
      const signsRef = ref(storage, 'gs://elite-ios-a6a68.appspot.com/signs/all-signs.json');
      try {
        // Try to get the download URL - if it succeeds, the file exists
        await getDownloadURL(signsRef);
        console.log('Signs file already exists, not creating a new one');
        return true;
      } catch (error) {
        // Only proceed if the file doesn't exist
        if (error.code === 'storage/object-not-found') {
          console.log('Creating signs file...');
          const emptySigns = [];
          const jsonString = JSON.stringify(emptySigns);
          const blob = new Blob([jsonString], { type: 'application/json' });
          const file = new File([blob], 'all-signs.json', { type: 'application/json' });
          
          try {
            await uploadBytes(signsRef, file);
            console.log('Empty signs file created successfully');
            return true;
          } catch (uploadError) {
            if (uploadError.code === 'storage/unauthorized') {
              console.error('Unauthorized to upload file:', uploadError);
              setError('You do not have permission to create the signs file. Please check your authentication.');
              return false;
            } else if (uploadError.code === 'storage/quota-exceeded') {
              console.error('Storage quota exceeded:', uploadError);
              setError('Storage quota exceeded. Please contact support.');
              return false;
            } else if (uploadError.code === 'storage/retry-limit-exceeded' || 
                      (uploadError.message && uploadError.message.includes('429'))) {
              console.error('Rate limit exceeded:', uploadError);
              setError('Too many requests. Please wait a moment and try again.');
              // Wait before retrying
              await new Promise(resolve => setTimeout(resolve, RATE_LIMIT_DELAY));
              return false;
            } else {
              console.error('Error uploading file:', uploadError);
              setError(`Failed to create signs file: ${uploadError.message}`);
              return false;
            }
          }
        } else {
          // Some other error occurred
          console.error('Error checking if file exists:', error);
          setError(`Error checking if signs file exists: ${error.message}`);
          return false;
        }
      }
    } catch (error) {
      console.error('Error creating signs file:', error);
      setError(`Failed to create signs file: ${error.message}`);
      return false;
    }
  };

  const fetchCurrentSigns = async () => {
    console.log('Starting fetchCurrentSigns...');
    let retryAttempt = 0;
    let lastError = null;
    
    const attemptFetch = async () => {
      let timeoutId = null; // Declare timeoutId here
      try {
        // Check authentication first (still good practice)
        const isAuth = await checkAuthentication();
        if (!isAuth) {
          setError('Authentication failed. Please sign in again.');
          setLoading(false);
          return false;
        }
        
        // Check if the user has the necessary permissions (still good practice)
        const hasPermissions = await checkUserPermissions();
        if (!hasPermissions) {
          return false;
        }

        // Log authentication state
        console.log('Current user:', {
          uid: auth.currentUser.uid,
          email: auth.currentUser.email,
          isAnonymous: auth.currentUser.isAnonymous,
        });

        // Get the file directly using Firebase Storage
        const signsRef = ref(storage, 'signs/all-signs.json');
        try {
          console.log('Starting download via Firebase Storage...');
          setLoading(true);
          setError(null);
          setDownloadProgress(0);

          // Function to attempt download with retry logic
          const attemptDownload = async (retryCount = 0) => {
            try {
              console.log(`Download attempt ${retryCount + 1}...`);
              
              // Get the file metadata first
              const metadata = await getMetadata(signsRef);
              const totalSize = metadata.size;
              console.log('File size:', totalSize, 'bytes');

              // Download the file as a blob
              console.log('Downloading file...');
              const blob = await getBlob(signsRef);
              console.log('Download completed');

              // Update progress to 100% since we got the whole file
              setDownloadProgress(100);

              // Convert blob to text
              console.log('Converting to text...');
              const text = await blob.text();
              console.log('Text conversion completed');

              // Parse JSON
              console.log('Parsing JSON...');
              const data = JSON.parse(text);
              console.log('JSON parsing completed');

              if (!Array.isArray(data)) {
                throw new Error('Downloaded data is not an array');
              }

              console.log('Successfully downloaded and parsed signs data. Length:', data.length);
              setCurrentSigns(data);
              setError(null);
              setLoading(false);
              return true;

            } catch (error) {
              console.error(`Download attempt ${retryCount + 1} failed:`, error);
              
              // If we haven't exceeded max retries, try again
              if (retryCount < MAX_RETRIES - 1) {
                const backoffDelay = RETRY_DELAY * Math.pow(2, retryCount);
                console.log(`Retrying in ${backoffDelay/1000} seconds...`);
                setError(`Download failed. Retrying in ${backoffDelay/1000} seconds...`);
                await delay(backoffDelay);
                return attemptDownload(retryCount + 1);
              }
              
              // If we've exhausted retries, throw the error
              throw error;
            }
          };

          // Start the download process
          return await attemptDownload();

        } catch (error) {
          console.error('Error during download:', error);
          
          // Handle specific Firebase Storage errors
          switch (error.code) {
            case 'storage/object-not-found':
              setError('Signs file not found. Please use the "Create Signs File" button to create it.');
              break;
            case 'storage/unauthorized':
              setError('You do not have permission to download this file.');
              break;
            case 'storage/canceled':
              setError('Download was canceled.');
              break;
            case 'storage/retry-limit-exceeded':
              setError('Too many requests. Please wait a moment and try again.');
              break;
            default:
              if (error.message.includes('timeout')) {
                setError('Download timed out. The file might be too large or the connection too slow.');
              } else if (error.message.includes('network') || error.message.includes('Network')) {
                setError('Network error occurred. Please check your internet connection and try again.');
              } else if (error.name === 'SyntaxError' && error.message.includes('JSON')) {
                setError('The downloaded file is not valid JSON. Please check the file format.');
              } else {
                setError(`Failed to download signs data: ${error.message}`);
              }
          }
          
          setLoading(false);
          setDownloadProgress(0);
          return false;
        }
      } catch (error) {
        if (timeoutId) clearTimeout(timeoutId); // Also clear timeout here if outer try fails early
        console.error('Error in attemptFetch wrapper:', error);
        lastError = error;
        setError(`Failed to fetch signs data: ${error.message}`);
        setLoading(false);
        setDownloadProgress(0);
        return false;
      }
    };

    // Try to fetch with exponential backoff
    while (retryAttempt < MAX_RETRIES) {
      console.log(`Attempt ${retryAttempt + 1} of ${MAX_RETRIES}`);
      const success = await attemptFetch();
      if (success) {
        return; // Success, exit the function
      }
      
      retryAttempt++;
      if (retryAttempt < MAX_RETRIES) {
        // Exponential backoff: 30s, 60s, 90s
        const backoffDelay = 30000 * retryAttempt;
        console.log(`Retrying in ${backoffDelay/1000} seconds...`);
        setError(`Download failed. Retrying in ${backoffDelay/1000} seconds...`);
        await delay(backoffDelay);
      }
    }
    
    // If we've exhausted all retries, show a more helpful error message
    setError(`Failed to fetch signs data after ${MAX_RETRIES} attempts. ${lastError ? `Error: ${lastError.message}` : 'Please check your network connection and try again.'}`);
    setLoading(false);
    setDownloadProgress(0);
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (file && file.type === 'text/csv') {
      // Check if the user has the necessary permissions
      checkUserPermissions().then(hasPermissions => {
        if (hasPermissions) {
          setCsvFile(file);
        }
      });
    } else {
      alert('Please upload a CSV file');
    }
  };

  const parseCSVLine = (line) => {
    const values = [];
    let currentValue = '';
    let withinQuotes = false;
    
    for (let i = 0; i < line.length; i++) {
      const char = line[i];
      
      if (char === '"') {
        if (withinQuotes && line[i + 1] === '"') {
          // Handle escaped quotes
          currentValue += '"';
          i++;
        } else {
          // Toggle quote mode
          withinQuotes = !withinQuotes;
        }
      } else if (char === ',' && !withinQuotes) {
        // End of field
        values.push(currentValue.trim());
        currentValue = '';
      } else {
        currentValue += char;
      }
    }
    
    // Push the last value
    values.push(currentValue.trim());
    return values;
  };

  const convertValue = (value, header) => {
    // Remove any surrounding quotes
    value = value.replace(/^"(.*)"$/, '$1');

    // Handle specific data types based on header
    switch (header) {
      case 'id':
      case 'postal_code':
      case 'screen_count':
      case 'min_duration':
      case 'max_duration':
      case 'four_week_impressions':
      case 'open_ooh_venue_category_id':
      case 'bidstream_impressions':
      case 'bidstream_inferred_impressions':
        return parseInt(value) || 0;
      case 'latitude':
      case 'longitude':
      case 'cpm':
      case 'impression_multiplier':
        return parseFloat(value) || 0;
      case 'supports_video':
      case 'supports_banner':
      case 'supports_audio':
      case 'supports_dynamic_creative':
        return value.toLowerCase() === 'true';
      case 'asset_name':
        return value.toString();
      default:
        return value;
    }
  };

  const convertCsvToJson = async () => {
    if (!csvFile) {
      alert('Please upload a CSV file first');
      return;
    }

    try {
      // Check if the user has the necessary permissions
      const hasPermissions = await checkUserPermissions();
      if (!hasPermissions) {
        return;
      }
      
      const text = await csvFile.text();
      const lines = text.split('\n').filter(line => line.trim());
      const headers = parseCSVLine(lines[0]);
      
      const jsonData = lines.slice(1).map(line => {
        const values = parseCSVLine(line);
        const rowData = {};
        
        headers.forEach((header, index) => {
          if (values[index] !== undefined) {
            rowData[header] = convertValue(values[index], header);
          }
        });

        // Find matching sign from current signs to preserve asset_image_url and name
        const matchingSign = currentSigns.find(sign => 
          sign.id === rowData.id || 
          sign.adunit_id === rowData.adunit_id
        );

        if (matchingSign) {
          // Always preserve the asset_name from the existing data
          rowData.asset_name = matchingSign.asset_name;
          
          // Only update asset_image_url if it's blank in the new data and exists in the old data
          if (!rowData.asset_image_url && matchingSign.asset_image_url) {
            rowData.asset_image_url = matchingSign.asset_image_url;
          }
        }

        return rowData;
      });

      setConvertedJson(jsonData);
    } catch (error) {
      console.error('Error converting CSV to JSON:', error);
      setError('Failed to convert CSV to JSON');
    }
  };

  const handleSaveToFirebase = async () => {
    if (!convertedJson) {
      alert('Please convert CSV to JSON first');
      return;
    }

    try {
      // Check if the user has the necessary permissions
      const hasPermissions = await checkUserPermissions();
      if (!hasPermissions) {
        return;
      }
      
      // First check if there's existing data
      const signsRef = ref(storage, 'gs://elite-ios-a6a68.appspot.com/signs/all-signs.json');
      let existingData = [];
      let hasExistingData = false;
      
      try {
        const downloadURL = await getDownloadURL(signsRef);
        const response = await fetch(downloadURL);
        if (response.ok) {
          existingData = await response.json();
          hasExistingData = Array.isArray(existingData) && existingData.length > 0;
        }
      } catch (error) {
        console.log('No existing data found or error fetching it:', error);
      }
      
      // If there's existing data, ask for confirmation
      if (hasExistingData) {
        const confirmOverwrite = window.confirm(
          `Warning: There are currently ${existingData.length} signs in the database. ` +
          `The new data contains ${convertedJson.length} signs. ` +
          `This will completely replace the existing data. Are you sure you want to continue?`
        );
        
        if (!confirmOverwrite) {
          console.log('Save operation cancelled by user');
          return;
        }
      }
      
      const jsonString = JSON.stringify(convertedJson, null, 2);
      const blob = new Blob([jsonString], { type: 'application/json' });
      const file = new File([blob], 'all-signs.json', { type: 'application/json' });
      
      await uploadBytes(signsRef, file);
      
      alert('Successfully updated signs data in Firebase Storage');
      fetchCurrentSigns(); // Refresh the current signs data
    } catch (error) {
      console.error('Error saving to Firebase:', error);
      setError('Failed to save to Firebase Storage');
    }
  };

  if (loading) {
    return (
      <div className="signs-converter">
        <h2>Signs Data Converter</h2>
        <div className="loading">
          <p>Loading current signs data...</p>
          {downloadProgress > 0 && (
            <div className="progress-bar">
              <div 
                className="progress-bar-fill" 
                style={{ width: `${downloadProgress}%` }}
              />
              <p>{downloadProgress}% downloaded</p>
            </div>
          )}
          <p>If this takes too long, you can proceed with uploading a new CSV file.</p>
          <button 
            onClick={() => setLoading(false)}
            className="skip-button"
          >
            Skip Loading
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="signs-converter">
      <h2>Signs Data Converter</h2>
      
      {error && (
        <div className="error">
          {error}
          <div className="error-actions">
            <button 
              onClick={() => {
                setLoading(true);
                setError(null);
                fetchCurrentSigns();
              }}
              className="retry-button"
            >
              Retry
            </button>
            <button 
              onClick={async () => {
                setLoading(true);
                setError(null);
                const created = await createSignsFile();
                if (created) {
                  fetchCurrentSigns();
                } else {
                  setLoading(false);
                }
              }}
              className="create-file-button"
            >
              Create Signs File
            </button>
          </div>
        </div>
      )}
      
      {currentSigns.length > 0 ? (
        <div className="current-signs-info">
          <h3>Current Signs Data</h3>
          <p>Total signs: {currentSigns.length}</p>
          <button 
            onClick={() => {
              setLoading(true);
              fetchCurrentSigns();
            }}
            className="refresh-button"
          >
            Refresh Data
          </button>
        </div>
      ) : (
        <div className="no-signs-info">
          <p>No existing signs data found. You can upload a new CSV file to start.</p>
          <button 
            onClick={async () => {
              setLoading(true);
              const created = await createSignsFile();
              if (created) {
                fetchCurrentSigns();
              } else {
                setLoading(false);
              }
            }}
            className="create-file-button"
          >
            Create Empty Signs File
          </button>
        </div>
      )}

      <div className="converter-section">
        <h3>Convert CSV to JSON</h3>
        <input
          type="file"
          accept=".csv"
          onChange={handleFileUpload}
          className="file-input"
        />
        <button 
          onClick={convertCsvToJson}
          disabled={!csvFile}
          className="convert-button"
        >
          Convert CSV to JSON
        </button>
      </div>

      {convertedJson && (
        <div className="preview-section">
          <h3>Converted JSON Preview</h3>
          <p>Total signs in converted data: {convertedJson.length}</p>
          <pre style={{ maxHeight: '300px', overflow: 'auto' }}>
            {JSON.stringify(convertedJson[0], null, 2)}
          </pre>
          <button 
            onClick={handleSaveToFirebase}
            className="save-button"
          >
            Save to Firebase Storage
          </button>
        </div>
      )}
    </div>
  );
};

export default SignsConverter; 